/**
 * Git-based filesystem checkpoints for the /rewind feature.
 *
 * Creates lightweight snapshots via `git stash create` at each agent turn,
 * allowing the user to restore the working tree to a previous state.
 */

import { spawnSync } from "node:child_process";
import { existsSync, readFileSync, renameSync, writeFileSync } from "node:fs";

/** A filesystem checkpoint associated with a session entry. */
export interface Checkpoint {
	/** Session entry ID this checkpoint is associated with. */
	entryId: string;
	/** Git stash commit ref. Empty string means working tree was clean (HEAD is the checkpoint). */
	stashRef: string;
	/** HEAD commit hash at checkpoint creation time. */
	headRef: string;
	/** ISO timestamp when the checkpoint was created. */
	timestamp: string;
}

/** Result of a filesystem restore operation. */
export interface RestoreResult {
	ok: boolean;
	/** Files that were changed by the restore. */
	filesChanged: string[];
	/** Error message if restore failed. */
	error?: string;
}

/** Run a git command synchronously and return stdout/status. */
function git(args: string[], cwd: string): { stdout: string; ok: boolean; stderr: string } {
	const result = spawnSync("git", args, {
		cwd,
		encoding: "utf8",
		stdio: ["ignore", "pipe", "pipe"],
		timeout: 10_000,
	});
	return {
		stdout: (result.stdout ?? "").trim(),
		ok: result.status === 0,
		stderr: (result.stderr ?? "").trim(),
	};
}

/** Check if the cwd is inside a git repository. */
export function isGitRepo(cwd: string): boolean {
	return git(["rev-parse", "--is-inside-work-tree"], cwd).ok;
}

/** Get the current HEAD commit hash. */
export function getHeadRef(cwd: string): string | undefined {
	const result = git(["rev-parse", "HEAD"], cwd);
	return result.ok ? result.stdout : undefined;
}

/**
 * Create a stash commit without storing it in the stash list.
 * Returns the commit hash, or empty string if working tree is clean.
 */
export function createStashCommit(cwd: string): string {
	const result = git(["stash", "create"], cwd);
	return result.ok ? result.stdout : "";
}

/** Check if a git object ref is still valid (not garbage-collected). */
export function isValidRef(cwd: string, ref: string): boolean {
	if (!ref) return false;
	return git(["cat-file", "-t", ref], cwd).ok;
}

/**
 * Get the list of files that differ between two refs.
 * If `toRef` is omitted, compares against the current working tree.
 */
export function getChangedFilesBetween(cwd: string, fromRef: string, toRef?: string): string[] {
	if (!fromRef) return [];
	const args = toRef ? ["diff", "--name-only", fromRef, toRef] : ["diff", "--name-only", fromRef];
	const result = git(args, cwd);
	return result.ok ? result.stdout.split("\n").filter(Boolean) : [];
}

/**
 * Get files that will change if we restore to a checkpoint.
 * Compares the checkpoint state against the current working tree.
 */
export function getFilesChangedByRewind(cwd: string, checkpoint: Checkpoint): string[] {
	if (!checkpoint.stashRef) {
		// Checkpoint was clean — changes are everything currently modified
		const result = git(["diff", "--name-only", "HEAD"], cwd);
		return result.ok ? result.stdout.split("\n").filter(Boolean) : [];
	}
	return getChangedFilesBetween(cwd, checkpoint.stashRef);
}

/**
 * Restore the working tree to match a checkpoint.
 *
 * Strategy:
 * 1. Reset all tracked files to HEAD (`git checkout -- .`)
 * 2. Apply the checkpoint stash on top (`git stash apply <ref>`)
 *
 * If the checkpoint stash ref is empty (working tree was clean at checkpoint time),
 * only step 1 is performed.
 */
export function restoreCheckpoint(cwd: string, checkpoint: Checkpoint): RestoreResult {
	// Verify the stash ref is still reachable (if one exists)
	if (checkpoint.stashRef && !isValidRef(cwd, checkpoint.stashRef)) {
		return { ok: false, filesChanged: [], error: "Checkpoint ref has been garbage-collected by git" };
	}

	// Warn if HEAD has moved since the checkpoint was created
	const currentHead = getHeadRef(cwd);
	if (currentHead && checkpoint.headRef && currentHead !== checkpoint.headRef) {
		// HEAD moved — stash apply may conflict, but we proceed anyway
	}

	// Determine which files will change before we modify anything
	const filesChanged = getFilesChangedByRewind(cwd, checkpoint);

	// Step 1: Reset tracked files to HEAD
	const resetResult = git(["checkout", "--", "."], cwd);
	if (!resetResult.ok) {
		return { ok: false, filesChanged: [], error: `Failed to reset working tree: ${resetResult.stderr}` };
	}

	// Step 2: Apply checkpoint stash (if working tree wasn't clean at checkpoint time)
	if (checkpoint.stashRef) {
		const applyResult = git(["stash", "apply", checkpoint.stashRef], cwd);
		if (!applyResult.ok) {
			return { ok: false, filesChanged, error: `Failed to apply checkpoint: ${applyResult.stderr}` };
		}
	}

	return { ok: true, filesChanged };
}

/**
 * Create a safety stash of the current working tree state.
 * Returns the stash ref (empty if working tree is clean).
 * This allows undoing a rewind operation.
 */
export function createSafetyStash(cwd: string): string {
	return createStashCommit(cwd);
}

// ---------------------------------------------------------------------------
// Checkpoint persistence
// ---------------------------------------------------------------------------

/** Get the checkpoints file path for a session file. */
export function getCheckpointsPath(sessionFile: string): string {
	return sessionFile.replace(/\.jsonl$/, ".checkpoints.json");
}

/** Load checkpoints from a JSON file. Returns empty map if file doesn't exist. */
export function loadCheckpoints(path: string): Map<string, Checkpoint> {
	const map = new Map<string, Checkpoint>();
	if (!existsSync(path)) return map;
	try {
		const data = JSON.parse(readFileSync(path, "utf8")) as Checkpoint[];
		for (const cp of data) {
			map.set(cp.entryId, cp);
		}
	} catch {
		// Corrupted file — start fresh
	}
	return map;
}

/** Save checkpoints to a JSON file. */
export function saveCheckpoints(filePath: string, checkpoints: Map<string, Checkpoint>): void {
	const tmpPath = `${filePath}.tmp`;
	writeFileSync(tmpPath, JSON.stringify([...checkpoints.values()]));
	renameSync(tmpPath, filePath);
}
